import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// A SchoolAdmin's view of their own school's team — via
// /api/admin/:schoolId/users. Anyone here can invite or remove teammates
// on this school (including themselves), but can't see other schools,
// change anyone's role, or reach FullAdmin-only management — that lives in
// the separate /admin/main area (see GlobalUsers.js, MainAdminDashboard.js).
function Users() {
  const { schoolId } = useParams();
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [showForm, setShowForm] = useState(false);

  const emptyForm = { username: '', full_name: '', email: '' };
  const [formData, setFormData] = useState(emptyForm);

  const apiBase = `/api/admin/${schoolId}`;

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${apiBase}/users`);
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load users');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setWarning('');

    try {
      const response = await axios.post(`${apiBase}/users`, formData);
      if (response.data?.warning) {
        setWarning(response.data.warning);
      }
      setFormData(emptyForm);
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to invite team member');
    }
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Remove "${username}" from this school? They'll keep access to any other schools they manage.`)) {
      return;
    }

    try {
      await axios.delete(`${apiBase}/users/${id}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData(emptyForm);
    setError('');
  };

  const handleInviteClick = () => {
    setFormData(emptyForm);
    setWarning('');
    setShowForm(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>School Team</h2>
        <button
          onClick={handleInviteClick}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Invite Team Member
        </button>
      </div>

      <p style={{ color: '#666', marginTop: '-1rem', marginBottom: '1.5rem' }}>
        Anyone here can manage orders, order dates, and teachers for this school, and can invite or remove other team members (including themselves).
      </p>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {warning && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          color: '#856404',
          border: '1px solid #ffeeba',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          ⚠️ {warning}
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3>Invite Team Member</h3>
            <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '-0.5rem' }}>
              A secure password will be generated automatically and emailed to them along with their login link.
            </p>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Username:
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Full Name:
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Email:
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No users found.
          </div>
        ) : (
          users.map(user => (
            <div
              key={user.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: user.is_active ? 'white' : '#f8f9fa'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: user.is_active ? '#333' : '#666' }}>
                    {user.full_name}
                    {!user.is_active && (
                      <span style={{ color: '#dc3545', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                        (Inactive)
                      </span>
                    )}
                    {user.username === currentUser?.username && (
                      <span style={{ color: '#666', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                        (You)
                      </span>
                    )}
                  </h3>
                  <p style={{ margin: '0', color: '#666', fontFamily: 'monospace' }}>
                    @{user.username}
                  </p>
                  {user.email && (
                    <p style={{ margin: '0.25rem 0 0 0', color: '#666' }}>
                      {user.email}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleDelete(user.id, user.username)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                Created: {formatDate(user.created_at)}
                {user.updated_at !== user.created_at && (
                  <span> • Updated: {formatDate(user.updated_at)}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Users;
